import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { PrivacyBlock } from "@/components/PrivacyBlock";
import { CalendarDays, CheckCircle, Clock, ChevronRight, ChevronLeft, Car, User, MapPin, Wrench, Search, AlertCircle, X, UserCheck, Tag } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
  { value: "turismo", label: "Turismo" },
  { value: "industrial", label: "Industrial" },
  { value: "autocaravana", label: "Autocaravana" },
];
const STEPS = [
  { num: 1, label: "Vehículo", icon: Car },
  { num: 2, label: "Contacto", icon: User },
  { num: 3, label: "Servicio", icon: Wrench },
  { num: 4, label: "Centro y fecha", icon: MapPin },
];

// Map JS getDay() → spanish day name in config
const DAY_MAP = ["domingo", "lunes", "martes", "miercoles", "jueves", "viernes", "sabado"];

function isEmailValid(email) {
  if (!email) return false; // Iter 204 (P1) — email ahora obligatorio
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isPhoneValid(phone) {
  const digits = phone.replace(/\D/g, "");
  return digits.length >= 9;
}

export default function BookAppointmentPage() {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const formTopRef = useRef(null);

  // Scroll suave al inicio del formulario en cada cambio de paso (incluye 'submitted').
  // No interfiere con el ScrollToTop global (que solo reacciona a cambios de ruta).
  useEffect(() => {
    const target = formTopRef.current;
    try {
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }
    } catch {
      // Fallback duro si el navegador no soporta smooth
      if (typeof window !== "undefined") window.scrollTo(0, 0);
    }
  }, [step]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lookupDone, setLookupDone] = useState(false);
  const [vehicleFound, setVehicleFound] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [legal, setLegal] = useState(false);
  const [commercialAccepted, setCommercialAccepted] = useState(false);
  const [config, setConfig] = useState(null);
  const [errors, setErrors] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dynamicCenters, setDynamicCenters] = useState([]);
  const [dynamicBrands, setDynamicBrands] = useState(["Otra marca"]);
  const [dynamicServices, setDynamicServices] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  // Promotion from URL params
  const [promo] = useState(() => {
    const id = searchParams.get("promo_id");
    if (!id) return null;
    return {
      id,
      titulo: searchParams.get("promo_titulo") || "",
      categoria: searchParams.get("promo_categoria") || "",
      precio: searchParams.get("promo_precio") || "",
      origen: searchParams.get("promo_origen") || "promocion_postventa",
    };
  });
  const [advisors, setAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [advisorConfig, setAdvisorConfig] = useState({ enabled: false, required: false, show_no_preference: true });
  const [privacyConfig, setPrivacyConfig] = useState(null);
  // Iter 147 — primera fecha disponible según los servicios seleccionados
  const [minDateInfo, setMinDateInfo] = useState(null); // {min_date, governing_service, governing_service_name, applied_delay_label}

  const [form, setForm] = useState({
    plate: "", brand: "", model_name: "", mileage_km: "", vehicle_category: "turismo",
    client_name: "", client_phone: "", client_email: "",
    center: "", center_name: "", time_slot: "",
    service_type: "", service_description: "",
    advisor_id: "", advisor_name: "",
    replacement_vehicle_needed: "no", replacement_vehicle_notes: "",
    services: [],  // M4: selector múltiple
  });

  // M4 + M3: si entra desde promoción, pre-seleccionar la promo como servicio
  useEffect(() => {
    if (!promo || !promo.id) return;
    const promoName = promo.titulo || promo.id;
    setForm(prev => {
      // Si ya está, no duplicar
      if ((prev.services || []).some(s => s.source === "promotion")) return prev;
      const promoService = {
        id: `promo_${promo.id}`,
        name: promoName,
        category: promo.categoria || "",
        source: "promotion",
      };
      const next = [promoService, ...(prev.services || [])];
      const names = next.map(s => s.name);
      const primary = names[0] || "";
      const compatType = names.length > 1 ? `${primary} + ${names.length - 1} servicios más` : primary;
      return { ...prev, services: next, service_type: compatType };
    });
  }, [promo]);

  // Fetch admin config, centers, brands and services on mount
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/config`).then(r => r.data).catch(() => null),
      axios.get(`${API}/centers?active_only=true`).then(r => r.data).catch(() => []),
      axios.get(`${API}/config/privacy`).then(r => r.data).catch(() => null),
      axios.get(`${API}/appointment-services/public`).then(r => r.data?.items || []).catch(() => []),
    ]).then(([cfg, ctrs, priv, svcCatalog]) => {
      setConfig(cfg);
      setPrivacyConfig(priv);
      setDynamicCenters(ctrs.map(c => ({ id: c.slug, name: c.name, address: c.address, zip: `${c.zip_code} ${c.city}` })));
      if (cfg) {
        const brandNames = (cfg.brands || []).filter(b => b.name).map(b => b.name);
        setDynamicBrands([...brandNames, "Otra marca"]);
        setAdvisorConfig({
          enabled: cfg.advisor_selection_enabled ?? false,
          required: cfg.advisor_selection_required ?? false,
          show_no_preference: cfg.advisor_show_no_preference ?? true,
        });
      }
      // Iter 147: catálogo nuevo de servicios. Fallback al config viejo si está vacío.
      if (svcCatalog && svcCatalog.length > 0) {
        setDynamicServices(svcCatalog);
      } else if (cfg) {
        const svcList = (cfg.services || [])
          .filter(s => typeof s === 'string' || (s.active !== false))
          .map((s, i) => typeof s === 'string'
            ? { slug: s.toLowerCase().replace(/\s+/g, "-"), name: s, centers: [], order: i, min_delay_value: 0, min_delay_unit: "business_days" }
            : { slug: s.slug || s.name?.toLowerCase().replace(/\s+/g, "-"), name: s.name, centers: s.centers || [], order: s.order || i, min_delay_value: s.min_delay_value || 0, min_delay_unit: s.min_delay_unit || "business_days" })
          .filter(s => s.name);
        setDynamicServices(svcList);
      }
    });
  }, []);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  // Multi-vehicle selector (cuando matrícula+teléfono devuelve varios vehículos)
  const [vehicleChoices, setVehicleChoices] = useState([]);
  const [pendingCustomer, setPendingCustomer] = useState(null);

  // Diccionario de marcas conocidas (canónicas). Permite hacer matching
  // tolerante (case/acentos) cuando la lupa devuelve "Alfa romeo" o "CITROEN".
  const KNOWN_BRANDS_CANONICAL = [
    "Alfa Romeo", "Citroën", "DS", "Fiat", "Jeep", "Opel", "Peugeot",
  ];
  const _normBrand = (s) => (s || "").toString()
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  /** Busca primero en dynamicBrands (config), luego en marcas canónicas.
   *  Devuelve el nombre canónico de la marca o null si no se conoce. */
  const matchBrand = (incoming) => {
    if (!incoming) return null;
    const norm = _normBrand(incoming);
    // 1) match en dynamicBrands (excluyendo "Otra marca")
    const fromConfig = dynamicBrands.find(b => _normBrand(b) === norm && b !== "Otra marca");
    if (fromConfig) return fromConfig;
    // 2) match en canónicas
    const canonical = KNOWN_BRANDS_CANONICAL.find(b => _normBrand(b) === norm);
    if (canonical) return canonical;
    // 3) prefijo: "alfa romeo tonale" empieza por "alfa romeo"
    for (const b of [...dynamicBrands.filter(x => x !== "Otra marca"), ...KNOWN_BRANDS_CANONICAL]) {
      const bn = _normBrand(b);
      if (bn && norm.startsWith(bn + " ")) return b;
    }
    return null;
  };

  /** Si el modelo viene concatenado con la marca ("Alfa romeo Tonale"),
   *  intenta quitar el prefijo de la marca para dejar solo el modelo. */
  const stripBrandPrefix = (model, brand) => {
    if (!model || !brand) return model || "";
    const m = _normBrand(model);
    const b = _normBrand(brand);
    if (m.startsWith(b + " ")) return model.slice(brand.length).trim();
    return model;
  };

  /** Normaliza la categoría de vehículo a las opciones del selector
   *  (turismo / industrial / autocaravana). Es tolerante a "Turismos",
   *  "TURISMO", etc. */
  const normalizeVehicleCategory = (raw) => {
    const t = _normBrand(raw);
    if (!t) return null;
    if (t.startsWith("turis")) return "turismo";
    if (t.startsWith("indus") || t.startsWith("comer")) return "industrial";
    if (t.startsWith("autoca") || t.startsWith("caravan") || t.startsWith("camper")) return "autocaravana";
    return null;
  };

  /** Aplica los datos verificados al formulario sin pisar lo que el usuario
   *  ya haya seleccionado (centro/asesor/servicios/promo/fecha). */
  const applyVerifiedMatch = (v, c) => {
    v = v || {}; c = c || {};
    // Marca: matching flexible. Si la marca devuelta no está en el selector,
    // la AÑADIMOS al listado para que el Select pueda renderizarla.
    let resolvedBrand = matchBrand(v.brand);
    if (!resolvedBrand && v.brand) {
      // Marca totalmente desconocida — usamos el valor tal cual y la añadimos.
      resolvedBrand = v.brand.toString().trim();
    }
    if (resolvedBrand) {
      setDynamicBrands(prev => {
        if (prev.some(b => _normBrand(b) === _normBrand(resolvedBrand))) return prev;
        const idx = prev.indexOf("Otra marca");
        const next = [...prev];
        if (idx >= 0) next.splice(idx, 0, resolvedBrand);
        else next.unshift(resolvedBrand);
        return next;
      });
    }

    // Modelo: quitar prefijo de la marca si viene concatenado
    const cleanModel = stripBrandPrefix(v.model || v.model_name || "", resolvedBrand || v.brand || "");

    // Categoría de vehículo: normalizar a opciones válidas del selector
    const resolvedCategory = normalizeVehicleCategory(v.vehicle_type);

    setForm(prev => ({
      ...prev,
      plate: (v.plate || prev.plate || "").toUpperCase(),
      brand: resolvedBrand || prev.brand,
      model_name: cleanModel || prev.model_name,
      mileage_km: (v.kilometers && String(v.kilometers).trim()) || prev.mileage_km,
      vehicle_category: resolvedCategory || prev.vehicle_category,
      client_name: (c.name || prev.client_name || "").toUpperCase(),
      client_phone: c.phone || prev.client_phone,
      client_email: (c.email || prev.client_email || "").toLowerCase(),
      // NO tocamos: center, advisor_id, advisor_name, time_slot, service_type,
      // services, replacement_vehicle_*. Tampoco promociones (en otro state).
    }));
    setVehicleFound(true);
  };

  // Plate + phone lookup (privacy-safe, doble verificación)
  // Llama al endpoint unificado /appointments/lookup-vehicle que busca en este
  // orden: vehicles → appointments → contact_submissions → legacy_leads.
  // SIEMPRE requiere matrícula + teléfono. Si falta uno, no busca.
  const lookupPlate = async () => {
    const normalized = form.plate.replace(/[\s-]/g, "").toUpperCase();
    const phoneClean = form.client_phone.replace(/[\s()+\-]/g, "");
    if (normalized.length < 4) return;
    if (phoneClean.length < 9) {
      setErrors(prev => ({ ...prev, client_phone: "Introduce matrícula y teléfono móvil para verificar tus datos." }));
      return;
    }
    set("plate", normalized);
    setLookupLoading(true);
    setVehicleChoices([]);
    setPendingCustomer(null);
    try {
      const res = await axios.post(`${API}/appointments/lookup-vehicle`, {
        plate: normalized, phone: phoneClean,
      });
      const data = res.data || {};
      if (data.found && data.verified) {
        if (data.multiple && Array.isArray(data.vehicles)) {
          // Mostrar selector — al elegir se invoca applyVerifiedMatch
          setVehicleChoices(data.vehicles);
          setPendingCustomer(data.customer || {});
          setVehicleFound(false);
        } else {
          applyVerifiedMatch(data.vehicle || {}, data.customer || {});
        }
      } else {
        setVehicleFound(false);
      }
      setLookupDone(true);
    } catch {
      setLookupDone(true);
      setVehicleFound(false);
    } finally {
      setLookupLoading(false);
    }
  };

  // Selección entre varios vehículos (multi-match)
  const pickVehicle = (v) => {
    applyVerifiedMatch(v, pendingCustomer || {});
    setVehicleChoices([]);
  };

  // Fetch advisors when center changes
  useEffect(() => {
    if (!form.center || !advisorConfig.enabled) { setAdvisors([]); return; }
    setAdvisorsLoading(true);
    axios.get(`${API}/advisors/by-center/${form.center}`)
      .then(r => setAdvisors(r.data))
      .catch(() => setAdvisors([]))
      .finally(() => setAdvisorsLoading(false));
  }, [form.center, advisorConfig.enabled]);

  // Compute available slots from API for selected date + center + advisor
  useEffect(() => {
    if (!date || !form.center) { setAvailableSlots([]); setSuggestions([]); return; }
    const dateStr = format(date, "yyyy-MM-dd");
    setSlotsLoading(true);
    const advParam = form.advisor_id ? `&advisor_id=${form.advisor_id}` : "";
    Promise.all([
      axios.get(`${API}/appointments/availability?date=${dateStr}&center=${form.center}${advParam}`),
      axios.get(`${API}/analytics/suggestions?date=${dateStr}&center=${form.center}`).catch(() => ({ data: { suggestions: [] } })),
    ]).then(([slotsRes, sugRes]) => {
      if (slotsRes.data.closed) { setAvailableSlots([]); }
      else { setAvailableSlots(slotsRes.data.slots || []); }
      setSuggestions(sugRes.data.suggestions || []);
    })
      .catch(() => { setAvailableSlots([]); setSuggestions([]); })
      .finally(() => setSlotsLoading(false));
  }, [date, form.center, form.advisor_id]);

  // Check if a day is disabled in the calendar
  const isDateDisabled = (d) => {
    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    // Iter 147 — bloquear fechas anteriores al plazo mínimo del servicio
    if (minDateInfo?.min_date) {
      const md = new Date(minDateInfo.min_date + "T00:00:00");
      if (d < md) return true;
    }
    if (!config?.working_hours) return d.getDay() === 0; // fallback: disable sunday
    const dayName = DAY_MAP[d.getDay()];
    const hours = config.working_hours[dayName];
    return !hours || hours.toLowerCase() === "cerrado";
  };

  // Iter 147 — recalcular min_date cada vez que cambien los servicios o el centro
  useEffect(() => {
    const slugs = (form.services || [])
      .map(s => s.slug || dynamicServices.find(d => d.name === s.name)?.slug)
      .filter(Boolean);
    if (slugs.length === 0) {
      setMinDateInfo(null);
      return;
    }
    let cancelled = false;
    axios.post(`${API}/appointment-services/min-date`, {
      service_slugs: slugs,
      center: form.center || "",
    }).then(r => {
      if (cancelled) return;
      setMinDateInfo(r.data);
      // Si la fecha ya estaba elegida y ya no cumple el plazo → resetear
      if (date && r.data?.min_date) {
        const md = new Date(r.data.min_date + "T00:00:00");
        if (date < md) {
          setDate(null);
          set("time_slot", "");
          toast.info(`Fecha reseteada: el nuevo servicio requiere mínimo ${r.data.applied_delay_label}.`);
        }
      }
    }).catch(() => { /* silencioso: backend ya valida en submit */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.services, form.center]);

  // Iter 147 — comprobar compatibilidad de un centro con los servicios elegidos
  const centerCompatibleWithServices = (centerSlug) => {
    const selected = form.services || [];
    if (selected.length === 0) return true;
    for (const sel of selected) {
      const def = dynamicServices.find(d => d.slug === sel.slug || d.name === sel.name);
      if (!def) continue;
      const centers = def.centers || [];
      if (centers.length === 0) continue; // sin restricción
      if (!centers.includes(centerSlug)) return false;
    }
    return true;
  };

  // Iter 236 (P1) — Validación con mensajes específicos que cumplen la spec.
  // Cada key coincide con `data-error-key` en el wrapper del campo, lo que
  // permite hacer scroll+focus preciso desde `goNext`/`handleSubmit`.
  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.plate.trim() || form.plate.length < 4) e.plate = "Introduce la matrícula (mínimo 4 caracteres).";
      if (!form.brand) e.brand = "Selecciona la marca del vehículo.";
      if (!vehicleFound && !form.model_name.trim()) e.model_name = "Introduce el modelo del vehículo.";
      // Iter 236: el teléfono en el paso 1 es OPCIONAL (solo para pre-búsqueda).
      // No bloquear el avance si no se ha rellenado.
    }
    if (s === 2) {
      if (!form.client_name.trim()) e.client_name = "Introduce tu nombre y apellidos.";
      if (!form.client_phone.trim()) e.client_phone = "Introduce un teléfono válido.";
      else if (!isPhoneValid(form.client_phone)) e.client_phone = "Introduce un teléfono válido (mínimo 9 dígitos).";
      if (!form.client_email.trim()) e.client_email = "Introduce un correo electrónico válido.";
      else if (!isEmailValid(form.client_email)) e.client_email = "Introduce un correo electrónico válido.";
    }
    if (s === 3) {
      if (!form.service_type || (form.services || []).length === 0) e.service_type = "Selecciona al menos un servicio.";
    }
    if (s === 4) {
      if (!form.center) e.center = "Selecciona un centro.";
      if (!date) e.date = "Selecciona una fecha disponible.";
      if (!form.time_slot) e.time_slot = "Selecciona una hora disponible.";
      if (advisorConfig.enabled && advisorConfig.required && !form.advisor_id) e.advisor_id = "Selecciona un asesor.";
      if (!legal) e.legal = "Debes aceptar el aviso legal para continuar.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Iter 236 (P1) — Orden de campos por paso (para scroll al primer error).
  const ERROR_ORDER = {
    1: ["plate", "brand", "model_name"],
    2: ["client_name", "client_phone", "client_email"],
    3: ["service_type"],
    4: ["center", "advisor_id", "date", "time_slot", "legal"],
  };

  const scrollToFirstError = (errObj, s) => {
    const order = ERROR_ORDER[s] || [];
    const firstKey = order.find((k) => errObj[k]) || Object.keys(errObj)[0];
    if (!firstKey) return;
    // Diferimos al siguiente tick para que el DOM haya renderizado los aria-invalid
    setTimeout(() => {
      const wrapper = document.querySelector(`[data-error-key="${firstKey}"]`);
      if (!wrapper) return;
      try {
        wrapper.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        wrapper.scrollIntoView();
      }
      // Intentar dar foco al primer control focalizable dentro
      const focusable = wrapper.querySelector(
        "input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])"
      );
      if (focusable && typeof focusable.focus === "function") {
        try { focusable.focus({ preventScroll: true }); } catch { focusable.focus(); }
      }
    }, 60);
  };

  const goNext = () => {
    const e = {};
    // Reutilizamos validateStep: si falla, `errors` ya se ha llenado. Necesitamos
    // acceder al objeto local para pasarlo a scrollToFirstError.
    const ok = validateStep(step);
    if (ok) { setStep(step + 1); return; }
    // Reconstruir errores localmente para el scroll (el state aún no está actualizado
    // en este render). Simplificamos: usamos setTimeout para leerlo tras el commit.
    setTimeout(() => {
      // Volver a validar para obtener los errores frescos (idempotente).
      const local = {};
      const s = step;
      if (s === 1) {
        if (!form.plate.trim() || form.plate.length < 4) local.plate = 1;
        if (!form.brand) local.brand = 1;
        if (!vehicleFound && !form.model_name.trim()) local.model_name = 1;
      }
      if (s === 2) {
        if (!form.client_name.trim()) local.client_name = 1;
        if (!form.client_phone.trim() || !isPhoneValid(form.client_phone)) local.client_phone = 1;
        if (!form.client_email.trim() || !isEmailValid(form.client_email)) local.client_email = 1;
      }
      if (s === 3) {
        if (!form.service_type || (form.services || []).length === 0) local.service_type = 1;
      }
      if (s === 4) {
        if (!form.center) local.center = 1;
        if (!date) local.date = 1;
        if (!form.time_slot) local.time_slot = 1;
        if (advisorConfig.enabled && advisorConfig.required && !form.advisor_id) local.advisor_id = 1;
        if (!legal) local.legal = 1;
      }
      scrollToFirstError(local, s);
    }, 0);
  };

  const handleDateSelect = (d) => {
    setDate(d);
    set("time_slot", ""); // reset time when date changes
    // Iter 236: al elegir fecha, retirar el error de fecha inmediatamente.
    setErrors(prev => ({ ...prev, date: undefined }));
  };

  // Iter 236 (P1) — Diferenciar errores técnicos de validación en el submit.
  const [technicalError, setTechnicalError] = useState("");
  const handleSubmit = async () => {
    setTechnicalError("");
    if (!validateStep(4)) {
      goNext(); // reutiliza la lógica de scroll+focus
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        plate: form.plate.replace(/[\s-]/g, "").toUpperCase(),
        client_name: form.client_name.toUpperCase(),
        client_email: form.client_email.toLowerCase(),
        mileage_km: parseInt(form.mileage_km) || 0,
        date: date ? format(date, "yyyy-MM-dd") : "",
        ...(promo ? {
          promo_id: promo.id,
          promo_titulo: promo.titulo,
          promo_categoria: promo.categoria,
          promo_precio: promo.precio,
          promo_origen: promo.origen,
        } : {}),
      };
      await axios.post(`${API}/appointments/public`, payload);
      setSubmitted(true);
      toast.success("Solicitud enviada correctamente");
    } catch (err) {
      // Log estructurado para auditoría en devtools/consola.
      // eslint-disable-next-line no-console
      console.error("[booking-submit] Error técnico al enviar solicitud", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });
      const msg = "No hemos podido continuar en este momento. Inténtalo de nuevo o contacta con Ascauto.";
      setTechnicalError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // ═══ SUCCESS ═══
  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-16 px-4">
        <div className="max-w-md text-center" data-testid="booking-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase mb-4">Solicitud Recibida</h2>
          <p className="text-sm text-slate-600 leading-relaxed mb-8">
            {config?.form_success_message || "Hemos recibido tu solicitud de cita. Nuestro equipo revisara la disponibilidad y se pondra en contacto contigo para confirmar la fecha definitiva."}
          </p>
          <Button onClick={() => { setSubmitted(false); setStep(1); setForm({ plate:"",brand:"",model_name:"",mileage_km:"",vehicle_category:"turismo",client_name:"",client_phone:"",client_email:"",center:"",center_name:"",time_slot:"",service_type:"",service_description:"",advisor_id:"",advisor_name:"",replacement_vehicle_needed:"no",replacement_vehicle_notes:"",services:[] }); setDate(null); setLegal(false); setLookupDone(false); setVehicleFound(false); setErrors({}); }}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-6"
            data-testid="booking-new-btn">Solicitar otra cita</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 sm:py-16">
      <div ref={formTopRef} className="max-w-2xl mx-auto px-4 sm:px-6 scroll-mt-24" data-testid="booking-form-top">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="text-xs uppercase tracking-widest text-red-600 font-semibold">Solicitar cita</span>
          <h1 className="heading-industrial text-3xl sm:text-4xl font-bold text-slate-900 mt-2 uppercase" data-testid="booking-page-title">Reservar Cita</h1>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-between mb-10 px-2">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex flex-col items-center ${step >= s.num ? 'opacity-100' : 'opacity-40'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step === s.num ? 'bg-red-600 text-white' : step > s.num ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-semibold mt-1.5 hidden sm:block">{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px w-8 sm:w-16 lg:w-24 mx-1 sm:mx-2 ${step > s.num ? 'bg-slate-900' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="border-slate-200 rounded-sm shadow-sm">
          <CardContent className="p-5 sm:p-7">

            {/* Iter 236 (P1) — Banner general de errores de validación.
                Visible en cualquier paso cuando hay errores tras pulsar "Siguiente". */}
            {Object.values(errors).some(v => v) && (
              <div
                role="alert"
                aria-live="polite"
                className="mb-5 flex items-start gap-3 rounded-sm border border-red-300 bg-red-50 p-3 text-sm text-red-800"
                data-testid="booking-error-summary"
              >
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <div className="flex-1">
                  <p className="font-semibold uppercase tracking-wider text-xs">Revisa los campos señalados para continuar</p>
                  <p className="text-[11px] text-red-700 mt-0.5">
                    Hay {Object.values(errors).filter(v => v).length} campo(s) obligatorio(s) sin completar o con formato incorrecto.
                  </p>
                </div>
              </div>
            )}

            {/* Iter 236 (P1) — Banner de error técnico (distinto de validación). */}
            {technicalError && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-3 rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                data-testid="booking-technical-error"
              >
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="flex-1">{technicalError}</p>
              </div>
            )}

            {/* Promotion banner */}
            {promo && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-sm p-4 flex items-start gap-3" data-testid="promo-banner">
                <Tag className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider font-bold text-red-700 mb-0.5">Promocion seleccionada</p>
                  <p className="text-sm font-bold text-slate-900">{promo.titulo}</p>
                  <div className="flex items-center gap-3 mt-1 flex-wrap">
                    {promo.precio && <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-sm">{promo.precio}</span>}
                    {promo.categoria && <span className="text-[10px] uppercase tracking-wider text-slate-500">{promo.categoria}</span>}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ STEP 1 – VEHICULO ═══ */}
            {step === 1 && (
              <div className="space-y-5 animate-fade-in" data-testid="step-vehicle">
                <h2 className="heading-industrial text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <Car className="w-5 h-5 text-red-600" /> Datos del Vehiculo
                </h2>

                {/* Plate + phone lookup */}
                <div className="space-y-3" data-error-key="plate">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-plate-input">Matricula *</Label>
                  <Input
                    id="booking-plate-input"
                    value={form.plate}
                    onChange={(e) => { set("plate", e.target.value.toUpperCase().replace(/[\s-]/g, "")); setLookupDone(false); }}
                    onKeyDown={(e) => {
                      // Iter 212: Enter ejecuta búsqueda (mismo flujo que el click en la lupa).
                      // preventDefault evita cualquier submit accidental o avance de paso.
                      if (e.key === "Enter") { e.preventDefault(); lookupPlate(); }
                    }}
                    placeholder="1234ABC"
                    className={`rounded-sm h-11 border-slate-200 text-lg font-mono uppercase tracking-wider ${errors.plate ? 'border-red-400 focus:ring-red-400' : ''}`}
                    maxLength={10}
                    aria-invalid={!!errors.plate}
                    aria-describedby={errors.plate ? "error-plate-msg" : undefined}
                    required
                    data-testid="booking-plate-input"
                  />
                  {errors.plate && <p id="error-plate-msg" className="text-xs text-red-600" data-testid="error-plate">{errors.plate}</p>}

                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-phone-input">Telefono movil <span className="normal-case font-normal text-slate-400">(opcional, para verificar tu vehículo)</span></Label>
                  <div className="flex gap-2">
                    <Input
                      id="booking-phone-input"
                      value={form.client_phone}
                      onChange={(e) => { set("client_phone", e.target.value); setLookupDone(false); }}
                      onKeyDown={(e) => {
                        // Iter 212: Enter ejecuta búsqueda. Si faltan datos, lookupPlate()
                        // mostrará el mismo aviso que con click en la lupa.
                        if (e.key === "Enter") { e.preventDefault(); lookupPlate(); }
                      }}
                      placeholder="611 222 333"
                      className={`rounded-sm h-11 border-slate-200 font-mono flex-1 ${errors.client_phone ? 'border-red-400' : ''}`}
                      data-testid="booking-phone-input"
                    />
                    <Button
                      type="button"
                      onClick={lookupPlate}
                      disabled={form.plate.length < 4 || form.client_phone.replace(/[\s()-]/g, "").length < 9 || lookupLoading}
                      variant="outline"
                      className="rounded-sm h-11 px-4 border-slate-300"
                      aria-label="Buscar vehículo por matrícula y teléfono"
                      title="Buscar vehículo por matrícula y teléfono (pulsa Enter en cualquiera de los campos)"
                      data-testid="booking-plate-search"
                    >
                      <Search className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </div>
                  {errors.client_phone && <p className="text-xs text-red-600">{errors.client_phone}</p>}
                </div>

                {/* Multi-vehicle selector */}
                {vehicleChoices.length > 0 && (
                  <div className="p-3 rounded-sm border border-blue-200 bg-blue-50 text-sm space-y-2" data-testid="multi-vehicle-selector">
                    <p className="font-semibold text-blue-900">Hemos encontrado varios vehículos asociados a este teléfono. Selecciona el tuyo:</p>
                    <div className="grid gap-1.5">
                      {vehicleChoices.map((v, i) => (
                        <button key={i} type="button" onClick={() => pickVehicle(v)}
                                className="text-left px-3 py-2 bg-white border border-slate-200 rounded-sm hover:border-blue-400 text-xs"
                                data-testid={`vehicle-choice-${i}`}>
                          <span className="font-mono font-bold">{v.plate}</span>
                          {(v.brand || v.model) && <span className="ml-2 text-slate-600">· {[v.brand, v.model].filter(Boolean).join(" ")}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lookup result */}
                {lookupDone && vehicleChoices.length === 0 && (
                  <div className={`p-3 rounded-sm border text-sm ${vehicleFound ? 'bg-green-50 border-green-200 text-green-800' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-testid="plate-lookup-result">
                    {vehicleFound ? (
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-semibold">Vehículo encontrado</p>
                          {form.brand && <p className="text-xs mt-0.5">{form.brand} {form.model_name}</p>}
                          {form.client_name && <p className="text-xs text-green-600">Cliente: {form.client_name}</p>}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <p>No hemos podido verificar el vehículo. Puedes continuar rellenando los datos manualmente.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Brand */}
                <div className="space-y-2" data-error-key="brand">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-brand-select">Marca *</Label>
                  <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
                    <SelectTrigger
                      id="booking-brand-select"
                      className={`rounded-sm h-10 border-slate-200 ${errors.brand ? 'border-red-400' : ''}`}
                      aria-invalid={!!errors.brand}
                      aria-describedby={errors.brand ? "error-brand-msg" : undefined}
                      data-testid="booking-brand-select"
                    ><SelectValue placeholder="Selecciona marca" /></SelectTrigger>
                    <SelectContent>
                      {dynamicBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {errors.brand && <p id="error-brand-msg" className="text-xs text-red-600" data-testid="error-brand">{errors.brand}</p>}
                </div>

                {/* Model */}
                <div className="space-y-2" data-error-key="model_name">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-model-input">
                    Modelo {!vehicleFound ? "*" : <span className="normal-case font-normal text-slate-400">(opcional)</span>}
                  </Label>
                  <Input
                    id="booking-model-input"
                    value={form.model_name}
                    onChange={(e) => set("model_name", e.target.value)}
                    placeholder="Ej: 500, Renegade, Corsa..."
                    className={`rounded-sm h-10 border-slate-200 ${errors.model_name ? 'border-red-400' : ''}`}
                    aria-invalid={!!errors.model_name}
                    aria-describedby={errors.model_name ? "error-model-msg" : undefined}
                    data-testid="booking-model-input"
                  />
                  {errors.model_name && <p id="error-model-msg" className="text-xs text-red-600" data-testid="error-model">{errors.model_name}</p>}
                </div>

                {/* KM + Type — ambos opcionales, aviso explícito */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-km-input">
                      Kilometros <span className="normal-case font-normal text-slate-400">(opcional)</span>
                    </Label>
                    <Input id="booking-km-input" type="number" value={form.mileage_km} onChange={(e) => set("mileage_km", e.target.value)} placeholder="60000"
                      className="rounded-sm h-10 border-slate-200" data-testid="booking-km-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-vtype-select">
                      Tipo vehiculo <span className="normal-case font-normal text-slate-400">(opcional)</span>
                    </Label>
                    <Select value={form.vehicle_category} onValueChange={(v) => set("vehicle_category", v)}>
                      <SelectTrigger id="booking-vtype-select" className="rounded-sm h-10 border-slate-200" data-testid="booking-vtype-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VEHICLE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ STEP 2 – CONTACTO ═══ */}
            {step === 2 && (
              <div className="space-y-5 animate-fade-in" data-testid="step-contact">
                <h2 className="heading-industrial text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <User className="w-5 h-5 text-red-600" /> Datos de Contacto
                </h2>
                <div className="space-y-2" data-error-key="client_name">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-name-input">Nombre y apellidos *</Label>
                  <Input
                    id="booking-name-input"
                    value={form.client_name}
                    onChange={(e) => set("client_name", e.target.value.toUpperCase())}
                    placeholder="NOMBRE Y APELLIDOS"
                    className={`rounded-sm h-10 border-slate-200 uppercase ${errors.client_name ? 'border-red-400' : ''}`}
                    aria-invalid={!!errors.client_name}
                    aria-describedby={errors.client_name ? "error-name-msg" : undefined}
                    required
                    data-testid="booking-name-input"
                  />
                  {errors.client_name && <p id="error-name-msg" className="text-xs text-red-600" data-testid="error-name">{errors.client_name}</p>}
                </div>
                <div className="space-y-2" data-error-key="client_phone">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-contact-phone-input">Telefono *</Label>
                  <Input
                    id="booking-contact-phone-input"
                    value={form.client_phone}
                    onChange={(e) => set("client_phone", e.target.value)}
                    placeholder="+34 600 000 000"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    className={`rounded-sm h-10 border-slate-200 ${errors.client_phone ? 'border-red-400' : ''}`}
                    aria-invalid={!!errors.client_phone}
                    aria-describedby={errors.client_phone ? "error-phone-msg" : undefined}
                    required
                    data-testid="booking-phone-input"
                  />
                  {errors.client_phone && <p id="error-phone-msg" className="text-xs text-red-600" data-testid="error-phone">{errors.client_phone}</p>}
                </div>
                <div className="space-y-2" data-error-key="client_email">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600" htmlFor="booking-email-input">Email *</Label>
                  <Input
                    id="booking-email-input"
                    value={form.client_email}
                    onChange={(e) => set("client_email", e.target.value.toLowerCase())}
                    placeholder="tu@email.com"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    required
                    aria-required="true"
                    aria-invalid={!!errors.client_email}
                    aria-describedby={errors.client_email ? "error-email-msg" : undefined}
                    className={`rounded-sm h-10 border-slate-200 lowercase ${errors.client_email ? 'border-red-400' : ''}`}
                    data-testid="booking-email-input"
                  />
                  {errors.client_email && <p id="error-email-msg" className="text-xs text-red-600" data-testid="error-email">{errors.client_email}</p>}
                </div>
              </div>
            )}

            {/* ═══ STEP 3 – SERVICIO ═══ */}
            {step === 3 && (
              <div className="space-y-5 animate-fade-in" data-testid="step-service">
                <h2 className="heading-industrial text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-red-600" /> Servicio
                </h2>

                <div className="space-y-2" data-testid="booking-services-block" data-error-key="service_type" role="group" aria-labelledby="services-label" aria-invalid={!!errors.service_type} aria-describedby={errors.service_type ? "error-service-msg" : undefined}>
                  <Label id="services-label" className="text-xs uppercase tracking-wider font-semibold text-slate-600">¿Qué servicios necesitas? * (puedes elegir varios)</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {dynamicServices.map(svc => {
                      const slug = svc.slug || svc.name;
                      const name = svc.name;
                      const selected = (form.services || []).some(x => x.slug === slug || x.name === name);
                      const isPromo = (form.services || []).find(x => (x.slug === slug || x.name === name) && x.source === "promotion");
                      return (
                        <button
                          key={slug}
                          type="button"
                          onClick={() => {
                            const current = form.services || [];
                            const exists = current.find(x => x.slug === slug || x.name === name);
                            let next;
                            if (exists) {
                              if (exists.source === "promotion") return; // no desmarcar promo
                              next = current.filter(x => !(x.slug === slug || x.name === name));
                            } else {
                              next = [...current, { id: slug, slug, name, category: "", source: "manual" }];
                            }
                            const names = next.map(x => x.name);
                            set("services", next);
                            const primary = names[0] || "";
                            const compatType = names.length > 1 ? `${primary} + ${names.length - 1} servicios más` : primary;
                            set("service_type", compatType);
                          }}
                          data-testid={`booking-service-${slug}`}
                          className={
                            "rounded-sm border px-3 py-3 text-xs uppercase tracking-wider font-semibold transition-all text-left " +
                            (selected
                              ? (isPromo
                                ? "border-amber-500 bg-amber-50 text-amber-900"
                                : "border-red-500 bg-red-50 text-red-900")
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")
                          }
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span>{selected ? "✓ " : ""}{name}</span>
                            {svc.min_delay_value > 0 && (
                              <span className="text-[9px] font-normal normal-case text-slate-400">
                                +{svc.min_delay_value}{svc.min_delay_unit === "natural_days" ? "d" : "L"}
                              </span>
                            )}
                          </div>
                          {svc.help_text && (
                            <span className="block text-[10px] mt-1 text-slate-500 font-normal normal-case leading-tight">{svc.help_text}</span>
                          )}
                          {isPromo && <span className="block text-[9px] mt-0.5 text-amber-700 font-normal normal-case">(desde promoción)</span>}
                        </button>
                      );
                    })}
                  </div>
                  {errors.service_type && <p id="error-service-msg" className="text-xs text-red-600" data-testid="error-service">{errors.service_type}</p>}
                  {(form.services || []).length > 1 && (
                    <p className="text-[11px] text-slate-500">
                      Servicio principal: <strong>{form.services[0].name}</strong> · Total: {form.services.length} servicios
                    </p>
                  )}
                  {minDateInfo?.governing_service_name && (form.services || []).length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="booking-min-date-info">
                      <p className="text-xs text-blue-900">
                        <CalendarDays className="w-3.5 h-3.5 inline mr-1.5" />
                        Primera fecha disponible: <strong>{format(new Date(minDateInfo.min_date + "T00:00:00"), "dd/MM/yyyy")}</strong>
                      </p>
                      {(form.services || []).length > 1 && (
                        <p className="text-[11px] text-blue-700 mt-1">
                          Se aplica el plazo de mayor espera: <strong>{minDateInfo.governing_service_name}</strong> — {minDateInfo.applied_delay_label}.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Cuéntanos brevemente qué necesitas</Label>
                  <Textarea value={form.service_description} onChange={(e) => set("service_description", e.target.value)}
                    placeholder="Detalles, kilómetros, problemas que notas, etc."
                    className="rounded-sm border-slate-200 min-h-[100px]" data-testid="booking-description" />
                </div>

                {/* Vehículo de alquiler (Iter 154 — antes "sustitución") */}
                <div className="space-y-2" data-testid="booking-replacement-vehicle-block">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                    ¿Necesitas vehículo de alquiler?
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {[
                      { value: "no", label: "No" },
                      { value: "yes", label: "Sí" },
                      { value: "unknown", label: "No lo sé, quiero que me llamen" },
                    ].map((opt) => {
                      const active = form.replacement_vehicle_needed === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => set("replacement_vehicle_needed", opt.value)}
                          data-testid={`booking-rv-needed-${opt.value}`}
                          className={
                            "rounded-sm border h-11 px-3 text-xs uppercase tracking-wider font-semibold transition-all " +
                            (active
                              ? "border-amber-500 bg-amber-50 text-amber-900"
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")
                          }
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {form.replacement_vehicle_needed !== "no" && (
                    <div className="space-y-1.5 pt-2" data-testid="booking-rv-notes-wrapper">
                      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                        Comentarios sobre el vehículo de alquiler (opcional)
                      </Label>
                      <Textarea
                        value={form.replacement_vehicle_notes}
                        onChange={(e) => set("replacement_vehicle_notes", e.target.value)}
                        placeholder="Ejemplo: lo necesitaría si la reparación dura más de un día."
                        className="rounded-sm border-slate-200 min-h-[80px]"
                        data-testid="booking-rv-notes"
                        maxLength={500}
                      />
                      <p className="text-[10px] text-slate-400">
                        La disponibilidad no está garantizada. Nuestro equipo te confirmará antes de la cita.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ═══ STEP 4 – CENTRO Y FECHA ═══ */}
            {step === 4 && (
              <div className="space-y-5 animate-fade-in" data-testid="step-center">
                <h2 className="heading-industrial text-lg font-bold text-slate-900 uppercase flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-red-600" /> Centro y Fecha
                </h2>

                {/* Banner plazo mínimo */}
                {minDateInfo?.min_date && (form.services || []).length > 0 && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="step4-min-date-banner">
                    <p className="text-xs text-blue-900">
                      <CalendarDays className="w-3.5 h-3.5 inline mr-1.5" />
                      Primera fecha disponible según los servicios seleccionados: <strong>{format(new Date(minDateInfo.min_date + "T00:00:00"), "dd/MM/yyyy")}</strong>
                    </p>
                    {(form.services || []).length > 1 && minDateInfo.governing_service_name && (
                      <p className="text-[11px] text-blue-700 mt-1">
                        Se aplica el plazo de mayor espera: <strong>{minDateInfo.governing_service_name}</strong> — {minDateInfo.applied_delay_label}.
                      </p>
                    )}
                  </div>
                )}

                {/* Center selector */}
                <div className="space-y-2" data-error-key="center" role="group" aria-labelledby="center-label" aria-invalid={!!errors.center} aria-describedby={errors.center ? "error-center-msg" : undefined}>
                  <Label id="center-label" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Centro *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {dynamicCenters.map(c => {
                      const compatible = centerCompatibleWithServices(c.id);
                      const isSelected = form.center === c.id;
                      return (
                        <button key={c.id} type="button"
                          onClick={() => {
                            if (!compatible) return;
                            set("center", c.id); set("center_name", c.name);
                            set("advisor_id", ""); set("advisor_name", "");
                          }}
                          disabled={!compatible}
                          className={`text-left p-4 rounded-sm border-2 transition-all ${isSelected ? 'border-red-600 bg-red-50' : compatible ? 'border-slate-200 hover:border-slate-300' : 'border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed'}`}
                          data-testid={`center-option-${c.id}`}>
                          <p className="heading-industrial text-sm font-bold uppercase">{c.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{c.address}</p>
                          <p className="text-xs text-slate-400">{c.zip}</p>
                          {!compatible && (
                            <p className="text-[10px] text-red-600 mt-2 font-semibold uppercase">
                              Este servicio no está disponible en este centro
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {errors.center && <p id="error-center-msg" className="text-xs text-red-600" data-testid="error-center">{errors.center}</p>}
                </div>

                {/* Advisor selector */}
                {advisorConfig.enabled && form.center && (
                  <div className="space-y-2" data-testid="advisor-section" data-error-key="advisor_id">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                      <UserCheck className="w-3 h-3 inline mr-1" /> Asesor de servicio {advisorConfig.required ? "*" : ""}
                    </Label>
                    {advisorsLoading ? (
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm text-center">
                        <p className="text-xs text-slate-400">Cargando asesores...</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {advisorConfig.show_no_preference && (
                          <button type="button"
                            onClick={() => { set("advisor_id", ""); set("advisor_name", ""); set("time_slot", ""); }}
                            className={`w-full text-left p-3 rounded-sm border-2 transition-all text-sm ${!form.advisor_id ? 'border-red-600 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
                            data-testid="advisor-no-preference">
                            <p className="font-semibold text-slate-700">Sin preferencia / primero disponible</p>
                            <p className="text-xs text-slate-400 mt-0.5">Se asignara el primer asesor disponible</p>
                          </button>
                        )}
                        <div className={`grid gap-2 ${advisors.length > 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                          {advisors.map(a => (
                            <button key={a.id} type="button"
                              onClick={() => { set("advisor_id", a.id); set("advisor_name", a.name); set("time_slot", ""); }}
                              className={`text-left p-3 rounded-sm border-2 transition-all ${form.advisor_id === a.id ? 'border-red-600 bg-red-50' : 'border-slate-200 hover:border-slate-300'}`}
                              data-testid={`advisor-${a.id}`}>
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-xs font-bold">{a.name.split(" ").map(w => w[0]).join("").slice(0, 2)}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">{a.name}</p>
                                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Asesor de servicio</p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                        {advisors.length === 0 && !advisorConfig.show_no_preference && (
                          <p className="text-xs text-slate-400 text-center py-2">No hay asesores disponibles para este centro</p>
                        )}
                      </div>
                    )}
                    {errors.advisor_id && <p id="error-advisor-msg" className="text-xs text-red-600" data-testid="error-advisor">{errors.advisor_id}</p>}
                  </div>
                )}

                {/* Date: Calendar or Summary */}
                <div className="space-y-2" data-error-key="date" aria-invalid={!!errors.date} aria-describedby={errors.date ? "error-date-msg" : undefined}>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Fecha *</Label>
                  {!date ? (
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={handleDateSelect}
                      disabled={isDateDisabled}
                      locale={es}
                      className="rounded-sm border border-slate-200"
                      data-testid="booking-calendar"
                    />
                  ) : (
                    <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="date-summary">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center">
                          <CalendarDays className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 capitalize">
                            {format(date, "EEEE d 'de' MMMM yyyy", { locale: es })}
                          </p>
                          <p className="text-xs text-slate-500">Fecha seleccionada</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => { setDate(null); set("time_slot", ""); }}
                        className="text-xs text-slate-500 hover:text-red-600 h-8 px-2" data-testid="change-date-btn">
                        <X className="w-3 h-3 mr-1" /> Cambiar
                      </Button>
                    </div>
                  )}
                  {errors.date && <p id="error-date-msg" className="text-xs text-red-600" data-testid="error-date">{errors.date}</p>}
                </div>

                {/* Time slots from availability API */}
                {date && form.center && (
                  <div className="space-y-2" data-error-key="time_slot" aria-invalid={!!errors.time_slot} aria-describedby={errors.time_slot ? "error-time-msg" : undefined}>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600">
                      <Clock className="w-3 h-3 inline mr-1" /> Hora disponible *
                    </Label>
                    {slotsLoading ? (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm text-center">
                        <p className="text-sm text-slate-400">Consultando disponibilidad...</p>
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="time-slots-grid">
                        {availableSlots.filter(s => s.available).map(s => (
                          <button key={s.slot} type="button" onClick={() => set("time_slot", s.slot)}
                            className={`text-xs font-mono py-2.5 rounded-sm border transition-colors ${form.time_slot === s.slot ? 'bg-red-600 text-white border-red-600' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`}
                            data-testid={`slot-${s.slot}`}>{s.slot}</button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm text-center">
                        <p className="text-sm text-slate-500">{config?.form_closed_message || "No hay horario disponible para esta fecha."}</p>
                      </div>
                    )}
                    {/* Smart suggestions */}
                    {suggestions.length > 0 && availableSlots.filter(s => !s.available).length > availableSlots.length * 0.3 && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-sm" data-testid="smart-suggestions">
                        <p className="text-xs font-semibold text-blue-800 flex items-center gap-1.5 mb-2">
                          <Clock className="w-3.5 h-3.5" /> Horarios recomendados (menor ocupacion)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {suggestions.slice(0, 5).map(s => (
                            <button key={s.slot} type="button" onClick={() => set("time_slot", s.slot)}
                              className={`text-xs font-mono px-3 py-1.5 rounded-sm border transition-colors ${form.time_slot === s.slot ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-blue-200 text-blue-700 hover:bg-blue-100'}`}
                              data-testid={`suggestion-${s.slot}`}>
                              {s.slot} <span className="text-[9px] opacity-70 ml-1">{s.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {errors.time_slot && <p id="error-time-msg" className="text-xs text-red-600" data-testid="error-time">{errors.time_slot}</p>}
                  </div>
                )}

                {/* Summary */}
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-2 text-sm">
                  <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Resumen de tu solicitud</p>
                  {promo && (
                    <div className="mb-3 pb-3 border-b border-slate-200">
                      <div className="flex items-center gap-2">
                        <Tag className="w-3.5 h-3.5 text-red-600" />
                        <span className="text-xs uppercase tracking-wider font-bold text-red-700">Promocion</span>
                      </div>
                      <p className="text-sm font-bold text-slate-900 mt-1">{promo.titulo}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {promo.precio && <span className="text-xs font-bold text-red-600">{promo.precio}</span>}
                        {promo.categoria && <span className="text-[10px] text-slate-400 uppercase">{promo.categoria}</span>}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    <span className="text-slate-500">Matricula</span><span className="font-mono font-semibold text-slate-900">{form.plate}</span>
                    <span className="text-slate-500">Vehiculo</span><span className="font-medium text-slate-900">{form.brand} {form.model_name}</span>
                    <span className="text-slate-500">Cliente</span><span className="font-medium text-slate-900">{form.client_name}</span>
                    <span className="text-slate-500">Servicio</span><span className="font-medium text-slate-900">{form.service_type}</span>
                    <span className="text-slate-500">Centro</span><span className="font-medium text-slate-900">{form.center_name}</span>
                    {advisorConfig.enabled && (
                      <><span className="text-slate-500">Asesor</span><span className="font-medium text-slate-900">{form.advisor_name || "Sin preferencia"}</span></>
                    )}
                    <span className="text-slate-500">Fecha</span><span className="font-medium text-slate-900">{date ? format(date, "dd-MM-yyyy") : "-"}</span>
                    <span className="text-slate-500">Hora</span><span className="font-mono font-medium text-slate-900">{form.time_slot}</span>
                  </div>
                </div>

                {/* Legal & Privacy */}
                <div className="space-y-3 pt-2" data-error-key="legal">
                  <p className="text-[11px] text-slate-400 leading-relaxed border-l-2 border-slate-200 pl-3">
                    Queremos ofrecerte el mejor servicio posible, por lo que la fecha seleccionada para tu cita en nuestro taller es orientativa y esta sujeta a disponibilidad y carga de trabajo. Nos comprometemos a hacer todo lo posible para atender tu vehiculo en la fecha mas cercana a la solicitada. Una vez recibida tu solicitud, nuestro equipo se pondra en contacto contigo para confirmar la cita definitiva.
                  </p>

                  <PrivacyBlock
                    privacyText={privacyConfig?.privacy_form_text_appointment}
                    policyUrl={privacyConfig?.privacy_policy_url || "/politica-de-privacidad"}
                    contactEmail={privacyConfig?.privacy_contact_email}
                    checkboxText={privacyConfig?.privacy_checkbox_text || "He leido y acepto la"}
                    accepted={legal}
                    onAcceptedChange={(v) => { setLegal(v); setErrors(prev => ({...prev, legal: undefined})); }}
                    error={errors.legal}
                    showCommercial={true}
                    commercialText={privacyConfig?.privacy_commercial_text}
                    commercialAccepted={commercialAccepted}
                    onCommercialChange={setCommercialAccepted}
                  />
                </div>
              </div>
            )}

            {/* ═══ NAVIGATION ═══ */}
            <div className="flex justify-between items-center mt-8 pt-5 border-t border-slate-200">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-5 border-slate-300" data-testid="booking-prev-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
              ) : <div />}

              {step < 4 ? (
                <Button onClick={goNext}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-10 px-6"
                  data-testid="booking-next-btn">
                  Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs uppercase tracking-wider font-bold h-11 px-8"
                  data-testid="booking-submit-btn">
                  {loading ? "Enviando..." : "Enviar Solicitud"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
